import { client } from '@/api/http';
import { type AxiosRequestConfig, type AxiosResponse, HttpStatusCode } from 'axios';
import { errorDetail } from '@/api/errorDetail';
import { XRAY_HIP_SPINE_VIEW_IDS } from '@/api/xrays';
import { AppError } from '@/stores/appErrorStore';
import { type LumbarLevel } from '@/domain/anatomy/hipSpineLandmarks';

export type HipSpineLandmarkApiObject = {
    name: string;
    original_x: number;
    original_y: number;
    current_x: number;
    current_y: number;
    likelihood: number;
};

export type HipSpineLandmarksResponse = {
    landmarks: HipSpineLandmarkApiObject[];
    posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number];
};

export type HipSpineApprovedLandmarkApiObject = {
    landmarks: HipSpineLandmark[];
    posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number];
    lumbar_vertebra_end_plate: LumbarLevel;
};

export type HipSpineLandmark = {
    name: string;
    x: number;
    y: number;
};

/** Fetch current hip spine landmarks for a posture */
export async function getHipSpineLandmarks(
    caseId: number,
    hipSpineStudyId: number,
    posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number],
    config?: AxiosRequestConfig,
): Promise<HipSpineLandmarksResponse> {
    const { status, data }: AxiosResponse = await client.get<HipSpineLandmarksResponse>(
        `cases/${caseId}/hip-spine-studies/${hipSpineStudyId}/postures/${posture}/landmarks`,
        config,
    );

    if (status === HttpStatusCode.Ok) {
        return data;
    } else if (status === HttpStatusCode.NotFound || status === HttpStatusCode.Forbidden) {
        throw new AppError(
            `Hip Spine ${posture} landmarks not found` + errorDetail(data),
            `The Hip Spine ${posture} landmarks do not exist or you do not have permission to view them.`,
        );
    }

    throw new Error(`Failed to get hip spine ${posture} landmarks` + errorDetail(data));
}

export type HipSpineLandmarkUpdate = {
    name: string;
    current_x: number;
    current_y: number;
};

export type PutHipSpineLandmarksArgs = {
    landmarks: HipSpineLandmarkUpdate[];
    config?: AxiosRequestConfig;
};

/** Save updated hip spine landmarks for a posture */
export async function putHipSpineLandmarks(
    caseId: number,
    hipSpineStudyId: number,
    posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number],
    { landmarks, config }: PutHipSpineLandmarksArgs,
): Promise<HipSpineLandmarksResponse> {
    const { status, data }: AxiosResponse = await client.put<HipSpineLandmarksResponse>(
        `cases/${caseId}/hip-spine-studies/${hipSpineStudyId}/postures/${posture}/landmarks`,
        { landmarks },
        config,
    );

    if (status === HttpStatusCode.Ok) {
        return data;
    } else if (status === HttpStatusCode.NotFound || status === HttpStatusCode.Forbidden) {
        throw new AppError(
            `Hip Spine ${posture} landmarks update forbidden or not found` + errorDetail(data),
            `The Hip Spine ${posture} landmarks do not exist or you do not have permission to update them.`,
        );
    }

    throw new Error(`Failed to update hip spine ${posture} landmarks` + errorDetail(data));
}

/** Approve current hip spine landmarks for a posture, creating an immutable snapshot */
export async function approveHipSpineLandmarks(
    caseId: number,
    hipSpineStudyId: number,
    posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number],
): Promise<HipSpineApprovedLandmarkApiObject> {
    const { status, data }: AxiosResponse = await client.post<HipSpineApprovedLandmarkApiObject>(
        `cases/${caseId}/hip-spine-studies/${hipSpineStudyId}/postures/${posture}/landmarks/approve`,
    );

    if (status === HttpStatusCode.Created) {
        return data;
    } else if (status === HttpStatusCode.Forbidden) {
        throw new AppError(
            `Hip Spine ${posture} landmarks approve forbidden` + errorDetail(data),
            'You do not have permission to approve landmarks for this posture.',
        );
    }

    throw new Error(`Failed to approve hip spine ${posture} landmarks` + errorDetail(data));
}

/** Fetch approved hip spine landmarks for a posture, or null if none exist */
export async function getHipSpineApprovedLandmarks(
    caseId: number,
    hipSpineStudyId: number,
    posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number],
    config?: AxiosRequestConfig,
): Promise<HipSpineApprovedLandmarkApiObject | null> {
    const { status, data }: AxiosResponse = await client.get<HipSpineApprovedLandmarkApiObject>(
        `cases/${caseId}/hip-spine-studies/${hipSpineStudyId}/postures/${posture}/landmarks/approved`,
        config,
    );

    if (status === HttpStatusCode.Ok) {
        return data;
    } else if (status === HttpStatusCode.NotFound) {
        return null;
    } else if (status === HttpStatusCode.Forbidden) {
        throw new AppError(
            `Hip Spine ${posture} posture approved landmarks forbidden` + errorDetail(data),
            `You do not have permission to view the Hip Spine ${posture} posture approved landmarks.`,
        );
    }

    throw new Error(
        `Failed to get hip spine ${posture} posture approved landmarks` + errorDetail(data),
    );
}
